import {
  isObject, isString, isNumber, isEmpty,
} from 'lodash';
import { DateTime, Settings } from 'luxon';
import { SHOW_END_HOUR_INTERVAL } from '@sda/constants';
import { DEFAULT_TIMEZONE } from './lib/constants';
import type { PublicShow } from '@sda/interfaces/show';
import type { AlgoliaTimeStamp, DateTimeInput } from './interfaces';

/**
 * Returns a Luxon.DateTime object based on the data type of the value passed to it
 * If nothing is passed to this function, return the current time
 * @param  {string|number|Date|DateTime|AlgoliaTimeStamp} [obj]
 * @param  {string} [format]
 * @return {DateTime}
 */
export const getDateTime = (obj?: DateTimeInput, format = ''): DateTime => {
  if (!obj) {
    return DateTime.local();
  }

  if (DateTime.isDateTime(obj)) {
    return obj;
  }

  if (Object.prototype.toString.call(obj) === '[object Date]') {
    return DateTime.fromJSDate(obj as Date);
  }

  if (isString(obj)) {
    if (format) {
      return DateTime.fromFormat(obj, format);
    }

    return DateTime.fromISO(obj);
  }

  if (isNumber(obj)) {
    if (obj.toString().length > 10) {
      return DateTime.fromMillis(obj);
    }

    return DateTime.fromSeconds(obj);
  }

  if (isObject(obj)) {
    if (Object.prototype.hasOwnProperty.call(obj, '_seconds')) {
      const { _seconds: seconds } = obj as any;

      return DateTime.fromSeconds(+seconds);
    }

    if (Object.prototype.hasOwnProperty.call(obj, 'seconds')) {
      const { seconds } = obj as any;

      return DateTime.fromSeconds(seconds);
    }
  }

  return DateTime.local();
};

/**
 * Return timezone based on show details
 * @param  {PublicShow} show
 * @return {string}
 */
export const getTimezone = (
  show: Pick<PublicShow, 'timezoneOverride'>,
): string => {
  const { timezoneOverride } = show;

  return timezoneOverride || DEFAULT_TIMEZONE;
};

interface DateRange {
  startDate: Date | FirebaseFirestore.Timestamp | DateTime | AlgoliaTimeStamp;
  endDate?: Date | FirebaseFirestore.Timestamp | DateTime | AlgoliaTimeStamp;
}

/**
 * Takes a beacon and outputs its date range
 * @param  {Range} dateRange - object containing {start,end}Date
 * @return {string}
 */
export const getDateRange = (dateRange?: DateRange): string => {
  if (isEmpty(dateRange)) {
    return '';
  }

  const { startDate, endDate } = dateRange;

  const startDateObject = getDateTime(startDate);
  const endDateObject = getDateTime(endDate);

  let string = startDateObject.toFormat('MMM d');

  if (!endDate || startDateObject.equals(endDateObject)) {
    return string;
  }

  string = startDateObject.toFormat('MMM d');

  if (startDateObject.month !== endDateObject.month) {
    return `${string} - ${endDateObject.toFormat('MMM d')}`;
  }

  if (startDateObject.day === endDateObject.day) {
    return `${string}`;
  }

  return `${string} - ${endDateObject.toFormat('d')}`;
};

/**
 * Determines when the show payout should be made, depending on when the show ends or when the ticketing ends
 * @param  {PublicShow} show
 * @return {number}
 */
export const getShowPayoutTime = (show: Pick<PublicShow, 'startTime' | 'ticketEndTime'>) => {
  const INTERVAL = 24;

  const startTime = getDateTime(show.startTime);
  const defaultEndTime = startTime.plus({ hours: SHOW_END_HOUR_INTERVAL });

  const ticketEndTime = getDateTime(show.ticketEndTime);

  if (show.ticketEndTime && ticketEndTime > defaultEndTime) {
    return getDateTime(show.ticketEndTime).plus({ hours: INTERVAL });
  }

  return defaultEndTime.plus({ hours: INTERVAL });
};

export const getCurrentDefaultZoneName = (): string => (isString(Settings.defaultZone) ? Settings.defaultZone : Settings.defaultZone.name);

interface FormatRelativeDateOptions {
  readonly displayToday?: boolean;
  readonly condensed?: boolean;
}

/**
 * Given a source date or date string, return a date string in relation to the current date
 * @param  {DateTimeInput} dateInput
 * @return {string}
 */
export const formatRelativeDate = (dateInput: DateTimeInput, options?: FormatRelativeDateOptions) => {
  const { displayToday, condensed } = options || {};

  const dateTime = getDateTime(dateInput);
  const now = getDateTime();

  if (dateTime.hasSame(now, 'day') && dateTime.hasSame(now, 'month') && dateTime.hasSame(now, 'year')) {
    if (displayToday) {
      return 'Today';
    }

    return dateTime.toFormat('t');
  }

  if (condensed) {
    return dateTime.toRelative({ style: 'short' })
      .replace(/\s+/g, '')
      .replace(/ago/g, '')
      .replace(/day[s]*/, 'd')
      .replace(/wk[s]*/, 'w')
      .replace(/mo[s]*/, 'mo')
      .replace(/yr[s]*/, 'yr');
  }

  if (dateTime > now.startOf('day').minus({ days: 7 })) {
    return dateTime.toFormat('cccc');
  }

  if (dateTime.hasSame(now, 'year')) {
    return dateTime.toFormat('LLL d');
  }

  return dateTime.toFormat('DD');
};
